import { ref, computed, onMounted, watch } from 'vue'
import { consumeNavFeedback } from '@/composables/useDashboardNavFeedback'
import { useStore } from 'vuex'
import { useDonorPlan } from '@/composables/useDonorPlan'
import sortBy from 'lodash/sortBy'
import reverse from 'lodash/reverse'
import find from 'lodash/find'
import isNil from 'lodash/isNil'
import { getCurrentUkTaxYear } from '@/helpers/ukTaxYear'
import type { DonorFinancialYear } from '@/types/donorFinancialYear'

type UseDonorFinancialYearsOptions = {
  /** When false, caller must invoke loadDonations() (e.g. statements page combined load). */
  autoLoad?: boolean
}

/**
 * Loads and selects donor financial years from the existing donations API.
 */
export function useDonorFinancialYears(
  options: UseDonorFinancialYearsOptions = {}
) {
  const autoLoad = options.autoLoad !== false
  const store = useStore()
  const { isPaid, isPlanKnown } = useDonorPlan()

  const financialYears = computed(
    () => (store.getters.financialYears as DonorFinancialYear[] | null) ?? []
  )

  const hasCachedDonations = () => (financialYears.value?.length ?? 0) > 0

  const financialPeriod = ref<string | null>(null)
  const sortedYears = ref<DonorFinancialYear[]>([])
  const isReady = ref(hasCachedDonations())
  const isLoading = ref(!hasCachedDonations())
  const apiError = ref(false)
  const hasAnyDonations = ref(false)

  const token = computed(() => store.getters.token as string | null)

  const selectedYear = computed(() => {
    if (isNil(financialPeriod.value)) return null
    return find(sortedYears.value, { period: financialPeriod.value }) ?? null
  })

  const currentTaxYear = getCurrentUkTaxYear()

  const currentYear = computed(
    () =>
      find(sortedYears.value, { period: currentTaxYear.periodKey }) ??
      sortedYears.value[0] ??
      null
  )

  /** Latest issued statement tax year (always the tax year before the current one). */
  const latestStatementPeriodKey = `${currentTaxYear.startYear - 1}-${currentTaxYear.startYear}`

  const latestStatementYear = computed(() => {
    const priorYear = find(sortedYears.value, {
      period: latestStatementPeriodKey,
    })
    if (priorYear && priorYear.totalDonations > 0) return priorYear
    return (
      sortedYears.value.find(
        (y) => y.period !== currentTaxYear.periodKey && y.totalDonations > 0
      ) ??
      sortedYears.value.find((y) => y.totalDonations > 0) ??
      sortedYears.value[0] ??
      null
    )
  })

  const initialiseYearSelection = (
    years: DonorFinancialYear[],
    period: string | null = null
  ) => {
    sortedYears.value = reverse(sortBy(years, 'period'))

    hasAnyDonations.value = sortedYears.value.some(
      (y) => y.donations.length > 0
    )

    if (period) {
      financialPeriod.value = period
      return
    }

    let maxYear = 0
    sortedYears.value.forEach((fy) => {
      const endPart = parseInt(fy.period.split('-').pop() ?? '0', 10)
      if (endPart > maxYear) {
        maxYear = endPart
        financialPeriod.value = fy.period
      }
    })
  }

  const loadDonations = async () => {
    if (hasCachedDonations()) {
      initialiseYearSelection(financialYears.value)
      isReady.value = true
      isLoading.value = false
      return
    }

    // Do not short-circuit as free until GET /me/plan has settled — otherwise a
    // paid donor can permanently skip the donations fetch on first paint.
    if (!isPlanKnown.value) {
      isLoading.value = true
      isReady.value = false
      await store.dispatch('fetchDonorPlan')
    }

    // Free donors have no giving record to load; GET /me/donations returns 403
    // for them, so skip the fetch and mark the view ready with no donations.
    if (!isPaid.value) {
      isReady.value = true
      isLoading.value = false
      store.commit('SET_LOADING', false)
      return
    }

    isLoading.value = true
    try {
      await store.dispatch('fetchDonorDonations')
      initialiseYearSelection(financialYears.value ?? [])
      isReady.value = true
    } catch (error) {
      console.error(error)
      apiError.value = true
      // Claim guide and other paid views wait on isReady; a failed fetch must
      // still release the spinner so the page can render an empty year.
      isReady.value = true
    } finally {
      isLoading.value = false
      store.commit('SET_LOADING', false)
    }
  }

  if (hasCachedDonations()) {
    initialiseYearSelection(financialYears.value)
    isReady.value = true
    isLoading.value = false
  }

  // Re-sync when the store's donations change out-of-band while this view is already
  // mounted — e.g. a post-upgrade refresh fetches the newly-unlocked paid data into the
  // store. Without this, the page keeps its stale local sortedYears. Preserve any period
  // the donor has selected.
  watch(financialYears, (years) => {
    initialiseYearSelection(years, financialPeriod.value)
  })

  // If the donor upgrades mid-session (or plan arrives after a free short-circuit),
  // load the giving record that was previously skipped.
  watch(isPaid, (paid, wasPaid) => {
    if (paid && !wasPaid && !hasCachedDonations()) {
      void loadDonations()
    }
  })

  onMounted(() => {
    if (!autoLoad) return

    consumeNavFeedback()

    if (hasCachedDonations()) {
      store.commit('SET_LOADING', false)
      return
    }

    if (token.value === null) {
      setTimeout(() => loadDonations(), 2500)
    } else {
      loadDonations()
    }
  })

  const setFinancialPeriod = (period: string) => {
    financialPeriod.value = period
  }

  return {
    sortedYears,
    financialPeriod,
    selectedYear,
    currentYear,
    latestStatementYear,
    latestStatementPeriodKey,
    currentTaxYear,
    isReady,
    isLoading,
    apiError,
    hasAnyDonations,
    loadDonations,
    setFinancialPeriod,
  }
}
